import LiveTvIcon from "@mui/icons-material/LiveTv";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import {
  Alert,
  Box,
  IconButton,
  Modal,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { clickableProps } from "../utils/keyboard";
import { prettyPrintUrl } from "../utils/url";

type LiveViewContentProps = {
  url: string;
  height: string | number;
};

const LiveViewContent = ({ url, height }: LiveViewContentProps) => {
  return (
    <iframe
      src={url}
      sandbox="allow-same-origin allow-scripts"
      allow="clipboard-read; clipboard-write"
      style={{
        display: "block",
        width: "100%",
        height,
        border: "0px",
      }}
      title="Live View"
    />
  );
};

type Props = {
  liveViewUrl?: string;
  allowInteractive?: boolean;
  pageUrl?: string;
  onRefresh?: () => void;
  noLiveViewMessage?: string;
  height?: string | number;
  defaultHideControls?: boolean;
  onClose?: () => void;
  onMinimize?: () => void;
};

export const LiveView = ({
  liveViewUrl,
  allowInteractive,
  pageUrl,
  onRefresh,
  noLiveViewMessage = "No live view available.",
  height = "520px",
  onClose,
  onMinimize,
}: Props) => {
  const [interactive, setInteractive] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const url = useMemo(() => {
    if (!liveViewUrl) {
      return null;
    }

    if (allowInteractive) {
      const url = new URL(
        liveViewUrl.includes("liveview-2")
          ? liveViewUrl
          : liveViewUrl.replace("liveview", "liveview-2"),
      );

      if (interactive) {
        url.searchParams.set("interactive", "true");
      } else {
        url.searchParams.set("interactive", "false");
      }

      return url.toString();
    }

    return liveViewUrl;
  }, [liveViewUrl, allowInteractive, interactive]);

  if (!url) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: height,
          p: 3,
        }}
      >
        <Alert severity="info" sx={{ maxWidth: "600px" }}>
          {noLiveViewMessage}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      border="1px solid"
      borderColor="grey.400"
      borderRadius="12px"
      overflow="hidden"
      sx={{
        backgroundColor: "common.white",
        height,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Browser Header with Traffic Lights and Address Bar */}
      <Box
        sx={{
          backgroundColor: "grey.100",
          borderBottom: "1px solid",
          borderBottomColor: "grey.400",
          p: 1.5,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Traffic Light Buttons */}
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pl: 2 }}>
            <Tooltip title={onClose ? "Close" : "Close (disabled)"}>
              <Box
                {...(onClose ? clickableProps(onClose) : {})}
                onClick={onClose}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: onClose ? "#ff5f57" : "#d0d0d0",
                  border: `1px solid ${onClose ? "#e0443e" : "#b0b0b0"}`,
                  cursor: onClose ? "pointer" : "not-allowed",
                  opacity: onClose ? 1 : 0.5,
                  "&:hover": onClose
                    ? {
                        backgroundColor: "#ff4136",
                      }
                    : {},
                }}
              />
            </Tooltip>
            <Tooltip title={onMinimize ? "Minimize" : "Minimize (disabled)"}>
              <Box
                {...(onMinimize ? clickableProps(onMinimize) : {})}
                onClick={onMinimize}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: onMinimize ? "#ffbd2e" : "#d0d0d0",
                  border: `1px solid ${onMinimize ? "#dea123" : "#b0b0b0"}`,
                  cursor: onMinimize ? "pointer" : "not-allowed",
                  opacity: onMinimize ? 1 : 0.5,
                  "&:hover": onMinimize
                    ? {
                        backgroundColor: "#ffa500",
                      }
                    : {},
                }}
              />
            </Tooltip>
            <Tooltip title="Maximize (fullscreen)">
              <Box
                {...clickableProps(() => setIsMaximized(true))}
                onClick={() => setIsMaximized(true)}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "#28ca42",
                  border: "1px solid #1aad34",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#20a639",
                  },
                }}
              />
            </Tooltip>
          </Stack>
          {/* Address Bar with Link */}
          <Box sx={{ position: "relative", flex: 1, mx: 2 }}>
            <TextField
              value={prettyPrintUrl(pageUrl) || ""}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                readOnly: true,
                sx: {
                  backgroundColor: "common.white",
                  borderRadius: "6px",
                  fontSize: "14px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "grey.400",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "grey.400",
                  },
                },
                endAdornment: pageUrl && (
                  <Tooltip title="Open page in new tab">
                    <Link to={pageUrl} target="_blank">
                      <IconButton
                        aria-label="Open page in new tab"
                        size="small"
                        sx={{
                          p: 0.5,
                          mr: 0.5,
                          "&:hover": {
                            backgroundColor: "grey.200",
                          },
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Link>
                  </Tooltip>
                ),
              }}
            />
          </Box>

          {/* Browser Controls */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Refresh Live View">
              <IconButton
                aria-label="Refresh Live View"
                size="small"
                disabled={!onRefresh}
                onClick={() => {
                  if (onRefresh) {
                    onRefresh();
                  }
                }}
                sx={{
                  backgroundColor: "transparent",
                  border: "1px solid",
                  borderColor: "grey.400",
                  "&:hover": {
                    backgroundColor: "grey.200",
                  },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Open live view in new tab">
              <IconButton
                aria-label="Open live view in new tab"
                size="small"
                onClick={() => {
                  window.open(url, "_blank");
                }}
                sx={{
                  backgroundColor: "transparent",
                  border: "1px solid",
                  borderColor: "grey.400",
                  "&:hover": {
                    backgroundColor: "grey.200",
                  },
                }}
              >
                <LiveTvIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {allowInteractive && (
              <Tooltip title={interactive ? "Disable Interaction" : "Enable Interaction"}>
                <Switch
                  size="small"
                  icon={
                    <TouchAppIcon
                      color="primary"
                      fontSize="inherit"
                      sx={{
                        backgroundColor: "whitesmoke",
                        borderRadius: "50%",
                        padding: "2px",
                      }}
                    />
                  }
                  checkedIcon={
                    <TouchAppIcon
                      color="secondary"
                      fontSize="inherit"
                      sx={{
                        backgroundColor: "black",
                        borderRadius: "50%",
                        padding: "2px",
                      }}
                    />
                  }
                  checked={interactive}
                  onChange={(e) => setInteractive(e.target.checked)}
                />
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Browser Content Area */}
      <Box sx={{ backgroundColor: "common.white", flex: 1, minHeight: 0 }}>
        <LiveViewContent url={url} height="100%" />
      </Box>

      {/* Fullscreen Modal */}
      <Modal
        open={isMaximized}
        onClose={() => setIsMaximized(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          sx={{
            width: "95vw",
            height: "95vh",
            backgroundColor: "common.white",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            position: "relative",
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              backgroundColor: "grey.100",
              borderBottom: "1px solid",
              borderBottomColor: "grey.400",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Live View - {prettyPrintUrl(pageUrl)}
            </Typography>
            <IconButton
              aria-label="Exit fullscreen"
              onClick={() => setIsMaximized(false)}
              size="small"
              sx={{
                backgroundColor: "common.white",
                border: "1px solid",
                borderColor: "grey.400",
                "&:hover": {
                  backgroundColor: "grey.200",
                },
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                ✕
              </Box>
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box
            sx={{
              height: "calc(100% - 60px)",
              backgroundColor: "common.white",
            }}
          >
            <LiveViewContent url={url} height="100%" />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
